import { Link } from "react-router-dom";
import { Check, Cpu, Plug, Headset, TrendingUp, Rocket, PiggyBank, Handshake, Moon, Layers, LineChart, ShieldCheck, CalendarX2 } from "lucide-react";
import PublicLayout from "@/components/layout/PublicLayout";
import { Button } from "@/components/ui/button";

const BOOSTER_FEATURES = [
  "Gestione dinamica dei prezzi e minimum stay",
  "Prezzi sempre aggiornati fino a 365 giorni in avanti",
  "Listing ed ottimizzazione dei TUOI annunci",
  "Channel Manager per zero overbooking",
  "Booking engine e web site one page",
  "Messaggistica da una sola app e programmata",
  "Miglior posizionamento sui portali",
  "Tutte le configurazioni ed ottimizzazioni incluse",
  "Servizio fotografico professionale",
  "Ricevute di pagamento per gli ospiti",
  "Automazione self check-in",
  "Invio dati Portale Alloggiati Web",
  "Conservazione ricevute di invio Portale Alloggiati Web",
];

const BENEFITS = [
  { icon: CalendarX2, title: "Paghi solo quando ottieni il risultato", desc: "Nessun canone fisso: paghi il 4% solo sulle prenotazioni ricevute. Se la struttura non riceve prenotazioni o è chiusa, non paghi nulla." },
  { icon: PiggyBank, title: "Aumenta i ricavi del 30%", desc: "Il nostro servizio si ripaga da solo: grazie all'ottimizzazione di annunci, prezzi e canali aumentiamo le tue prenotazioni molto più del 4% della nostra commissione." },
  { icon: Cpu, title: "La migliore tecnologia, gestita da noi", desc: "Mettiamo a tua disposizione strumenti professionali di ultima generazione per gestire prenotazioni, tariffe e portali — configurati e gestiti interamente dal nostro team. Tu non devi imparare né usare alcun software." },
  { icon: Plug, title: "Più prenotazioni, soprattutto dirette", desc: "Ottimizziamo annunci e posizionamento sui portali e ti diamo un sito con booking engine: più prenotazioni ogni mese e sempre più ospiti che prenotano direttamente da te." },
  { icon: Headset, title: "Assistenza e formazione continua", desc: "Non un semplice supporto tecnico, ma un consulente reale sempre al tuo fianco per la gestione quotidiana della tua struttura." },
  { icon: TrendingUp, title: "I prezzi li gestiamo noi, sempre", desc: "Prezzi dinamici e minimum stay ottimizzati automaticamente fino a 365 giorni in avanti, per aumentare occupazione e ricavi. Nessun intervento manuale da parte tua." },
];

const REASONS = [
  { icon: Handshake, title: "Obiettivi condivisi", desc: "Il nostro successo dipende dal tuo. Siamo motivati a farti ottenere il maggior numero possibile di prenotazioni." },
  { icon: Moon, title: "Nessun costo quando non lavori", desc: "Se la struttura è chiusa o non riceve prenotazioni, non paghi commissioni. Un modello più equo rispetto ai tradizionali abbonamenti." },
  { icon: Layers, title: "Software professionale incluso", desc: "Hai a disposizione Channel Manager, PMS e Booking Engine, una piattaforma professionale che da sola avrebbe un costo significativo." },
  { icon: LineChart, title: "Revenue Management senza costi aggiuntivi", desc: "Analizziamo il mercato e ottimizziamo le tariffe per aumentare occupazione e ricavi, senza richiedere un ulteriore canone mensile." },
  { icon: ShieldCheck, title: "Più valore, meno rischio", desc: "Non paghi per la semplice disponibilità del servizio, ma per i risultati ottenuti. È un modello pensato per sostenere la crescita della tua attività senza appesantire i costi fissi." },
];

function PlanCard() {
  return (
    <div className="rounded-3xl bg-white border-2 border-brand-teal p-8 shadow-xl flex flex-col relative overflow-hidden" data-testid="plan-booster">
      <span className="absolute top-5 right-5 text-[11px] font-semibold px-3 py-1 rounded-full bg-brand-teal text-brand-violet" data-testid="booster-badge">Tutto incluso</span>
      <div className="flex items-center gap-2 text-brand-tealDark mb-3">
        <Rocket size={20} /><span className="text-xs font-mono uppercase tracking-[0.2em] font-semibold">Booster</span>
      </div>
      <div className="flex items-baseline gap-2">
        <span className="font-serif text-6xl text-brand-plum" data-testid="booster-price">4%</span>
        <span className="text-brand-plum/60">sul valore delle prenotazioni</span>
      </div>
      <p className="text-sm text-brand-plum/70 mt-3 leading-relaxed" data-testid="booster-target">
        Indicato per chi preferisce pagare solo quando ottiene il risultato e non pagare nulla quando la struttura non riceve prenotazioni o è chiusa.
      </p>
      <p className="text-sm text-brand-plum/70 mt-3 rounded-xl bg-brand-warm px-3 py-2" data-testid="booster-admin-fee">+ €120 / anno per casa vacanza o B&B di costo amministrativo</p>
      <ul className="mt-6 space-y-2.5 flex-1">
        {BOOSTER_FEATURES.map((f) => (
          <li key={f} className="flex items-start gap-2.5 text-sm text-brand-plum/75">
            <Check size={16} className="text-brand-teal shrink-0 mt-0.5" /> {f}
          </li>
        ))}
      </ul>
      <Link to="/contatti?plan=Booster" className="mt-7">
        <Button className="w-full rounded-full bg-brand-plum hover:bg-brand-violet text-white font-semibold h-12" data-testid="booster-cta">Scegli Booster</Button>
      </Link>
    </div>
  );
}

function ModelSection() {
  return (
    <section className="bg-brand-warm" data-testid="pricing-model">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 grid lg:grid-cols-[1.2fr_1fr] gap-10 items-start">
        <div className="space-y-5 text-brand-plum/75 leading-relaxed">
          <p className="text-xs font-mono uppercase tracking-[0.22em] font-semibold text-brand-teal">Un modello semplice e trasparente</p>
          <h2 className="text-3xl sm:text-4xl font-serif text-brand-plum">Se la tua struttura guadagna, guadagniamo anche noi</h2>
          <p>Con il nostro servizio per Case Vacanze e B&B abbiamo scelto un modello semplice e trasparente: una commissione calcolata esclusivamente sulle prenotazioni ricevute.</p>
          <p>Questo significa che i nostri interessi sono gli stessi dei tuoi: se la tua struttura guadagna, guadagniamo anche noi. Per questo lavoriamo ogni giorno per aumentare la tua visibilità, ottimizzare le tariffe e massimizzare le prenotazioni.</p>
          <p>A differenza dei tradizionali servizi a canone fisso, nel nostro prezzo sono già inclusi strumenti e competenze che normalmente comportano costi elevati, come il <strong className="text-brand-plum">Channel Manager, PMS e Booking Engine Octorate</strong>, oltre a un servizio professionale di <strong className="text-brand-plum">Revenue Management</strong> per ottimizzare prezzi e occupazione.</p>
        </div>
        <div className="rounded-3xl bg-brand-plum text-white p-8 md:p-9" data-testid="pricing-key-advantage">
          <p className="text-xs font-mono uppercase tracking-[0.22em] font-semibold text-brand-tealGlow mb-3">Il vantaggio più importante?</p>
          <h3 className="font-serif text-2xl sm:text-3xl mb-4">Se non ricevi prenotazioni, non paghi commissioni.</h3>
          <p className="text-white/75 text-sm leading-relaxed">Durante la bassa stagione, se decidi di chiudere la struttura per qualche settimana o semplicemente non hai ospiti, non continui a sostenere costi inutili. Con un piano a quota fissa, invece, il canone rimane da pagare anche quando la struttura non produce alcun fatturato.</p>
          <div className="mt-6 grid grid-cols-2 gap-3 text-sm">
            <div className="rounded-2xl bg-white/10 p-4"><p className="text-white/60 text-xs mb-1">Struttura chiusa</p><p className="font-semibold text-brand-tealGlow">Con Quattro: €0</p><p className="text-white/60 text-xs mt-1">A canone fisso: paghi comunque</p></div>
            <div className="rounded-2xl bg-white/10 p-4"><p className="text-white/60 text-xs mb-1">Nessuna prenotazione</p><p className="font-semibold text-brand-tealGlow">Con Quattro: €0</p><p className="text-white/60 text-xs mt-1">A canone fisso: paghi comunque</p></div>
          </div>
          <p className="mt-6 text-sm text-white/85 border-t border-white/15 pt-5">Il nostro obiettivo è ridurre i tuoi rischi e permetterti di investire solo quando il servizio genera risultati concreti.</p>
        </div>
      </div>
    </section>
  );
}

function ReasonsSection() {
  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24" data-testid="pricing-reasons">
      <h2 className="text-3xl sm:text-4xl font-serif text-brand-plum mb-10">Perché scegliere il nostro servizio a commissione?</h2>
      <ol className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
        {REASONS.map((r, i) => (
          <li key={r.title} className="rounded-2xl bg-white p-7 border border-brand-plum/10 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <span className="font-serif text-3xl text-brand-teal leading-none">{i + 1}</span>
              <r.icon className="text-brand-tealDark" size={20} />
            </div>
            <h3 className="font-serif text-lg text-brand-plum mb-2">{r.title}</h3>
            <p className="text-sm text-brand-plum/60 leading-relaxed">{r.desc}</p>
          </li>
        ))}
      </ol>
    </section>
  );
}

export default function Prezzi() {
  return (
    <PublicLayout>
      <section className="bg-brand-plum text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 md:pt-24 pb-24 md:pb-28 text-center">
          <p className="text-xs font-mono uppercase tracking-[0.22em] font-semibold text-brand-tealGlow mb-4">Commissione solo sui risultati</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif">Paghi solo quando ricevi prenotazioni</h1>
          <p className="mt-5 text-lg text-white/80 max-w-2xl mx-auto">
            Il partner ideale per i proprietari di case vacanze e piccoli B&B. Aumenteremo le tue prenotazioni molto più del 4% della nostra commissione. Un solo piano, tutto incluso: pensiamo noi alla tecnologia, ai portali e ai prezzi.
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 -mt-14 relative z-10">
        <div className="max-w-md mx-auto"><PlanCard /></div>
        <p className="text-center text-sm text-brand-plum/50 mt-5">+ IVA dove applicabile.</p>
      </section>

      <div className="mt-16 md:mt-20"><ModelSection /></div>
      <ReasonsSection />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 md:pb-28">
        <h2 className="text-3xl sm:text-4xl font-serif text-brand-plum mb-8">Cosa ottieni con Quattro</h2>
        <div className="grid gap-6 md:grid-cols-3">
          {BENEFITS.map((v) => (
            <div key={v.title} className="rounded-2xl bg-white p-7 border border-brand-plum/10 shadow-sm">
              <div className="w-12 h-12 rounded-xl bg-brand-tealLight flex items-center justify-center mb-4">
                <v.icon className="text-brand-tealDark" size={22} />
              </div>
              <h3 className="font-serif text-lg text-brand-plum mb-2">{v.title}</h3>
              <p className="text-sm text-brand-plum/60 leading-relaxed">{v.desc}</p>
            </div>
          ))}
        </div>
        <div className="mt-12 rounded-3xl bg-brand-violet text-white p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6" data-testid="pricing-final-cta">
          <div>
            <h3 className="font-serif text-2xl sm:text-3xl">Più valore, meno rischio.</h3>
            <p className="text-white/75 mt-2">Inizia senza costi fissi mensili: paghi il 4% solo sulle prenotazioni che ricevi.</p>
          </div>
          <Link to="/contatti?plan=Booster" className="shrink-0">
            <Button className="rounded-full bg-brand-teal hover:bg-brand-tealDark text-brand-violet font-semibold px-8 h-12" data-testid="pricing-final-cta-btn">Richiedi informazioni</Button>
          </Link>
        </div>
      </section>
    </PublicLayout>
  );
}
